import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { navLinks } from "@/content/coletor-publico-landing";

const LOGO_URL = "/logos/floraiq_logo_oficial.webp";

interface LandingHeaderProps {
  onNavigate?: (href: string) => void;
}

export default function LandingHeader({ onNavigate }: LandingHeaderProps) {
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      if (onNavigate) {
        onNavigate(href);
      } else {
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }
      setMobileOpen(false);
    },
    [onNavigate]
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-background-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-2 shrink-0"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img
              src={LOGO_URL}
              alt="FloraIQ"
              className="h-9 md:h-[34px] w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950"
                    : "text-white/80 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => navigate("/publico")}
              className={`px-4 py-2.5 text-sm font-semibold rounded-md transition-colors whitespace-nowrap border ${
                scrolled
                  ? "text-primary-600 border-primary-300 hover:bg-primary-50"
                  : "text-white border-white/40 hover:bg-white/10"
              }`}
            >
              Site Publico
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2.5 text-sm font-semibold rounded-md transition-colors whitespace-nowrap text-white bg-primary-500 hover:bg-primary-600"
            >
              Acessar plataforma
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-md transition-colors ${
              scrolled ? "hover:bg-background-100 text-foreground-700" : "hover:bg-white/10 text-white"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i
              className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-5 h-5 flex items-center justify-center`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-50 rounded-md transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-background-200">
              <button
                onClick={() => navigate("/publico")}
                className="px-4 py-2.5 text-sm font-semibold text-primary-600 border border-primary-300 rounded-md text-center transition-colors whitespace-nowrap hover:bg-primary-50"
              >
                Site Publico
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="px-4 py-2.5 text-sm font-semibold text-white rounded-md text-center transition-colors whitespace-nowrap bg-primary-500 hover:bg-primary-600"
              >
                Acessar plataforma
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}